import {
    NgModule,
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA,
} from '@angular/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';

import { SharedModule } from './shared/shared.module';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ApiMessageInterceptor } from './core/interceptors/api-message.interceptor';
import { SlowReportRequestInterceptor } from './core/interceptors/slow-report-request.interceptor';
import { TrackingInterceptor } from './core/interceptors/tracking.interceptor';
import { provideEventTracker, provideTrackingHttpInterceptor } from '@duocvuong/event-tracker/angular';
import localVi from '@angular/common/locales/vi';
import { registerLocaleData } from '@angular/common';
import { environment } from '@/environments/environment';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';

registerLocaleData(localVi);

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        SharedModule,
        AppRoutingModule,
        ToastrModule.forRoot({
            positionClass: 'toast-top-center',
            preventDuplicates: false,
        }),
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
    ],
    providers: [
        {provide: HTTP_INTERCEPTORS, useClass: SlowReportRequestInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: ApiMessageInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: TrackingInterceptor, multi: true},
        // Ghi hành vi sử dụng của nhân viên: phiên, màn hình theo router, header truy vết gắn vào API (nối lệnh ghi ở backend về lượt bấm trên admin).
        // Nền tảng cms: Ingest lấy tên nhân viên từ JWT (actor_id), không bao giờ ghi vào user_id. Kế hoạch: docs/guides/admin-audit.md (repo DvEventSourcing)
        provideEventTracker(() => {
            const t: { enabled?: boolean; endpoint?: string; writeKey?: string; configName?: string } = (environment as any).eventTracking;
            // SDK ném lỗi khi thiếu endpoint hoặc writeKey dù enabled=false, và lỗi đó làm app không khởi động được: môi trường chưa cấu hình thì tắt hẳn
            const on = !!(t?.enabled && t.endpoint && t.writeKey);
            return {
                endpoint: t?.endpoint || 'https://tracking-disabled.invalid/e',
                writeKey: t?.writeKey || 'disabled',
                platform: 'cms' as const,
                apiOrigins: [environment.baseURL],
                getAccessToken: () => localStorage.getItem('token') ?? undefined,
                enabled: on,
                // Có configName: chờ AppComponent lấy config từ ConfigurationV2 rồi đưa vào SDK (tracker.applyPolicy). Chưa có config, lỗi, hoặc quá 5 giây thì TẮT
                policy: on && t.configName ? ('wait' as const) : undefined,
                strict: false,
            };
        }),
        // Gốc của lời gọi API là event screen_viewed của màn hình đang xem (admin không có event riêng cho từng lần bấm): lệnh ghi ở backend có has_ui_parent = 1,
        // lệnh không có gốc là gọi API trực tiếp
        provideTrackingHttpInterceptor({ parentFromScreen: true }),
        provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
        provideMessaging(() => getMessaging()),

    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    bootstrap: [AppComponent]
})
export class AppModule { }